import React from 'react';

/**
 * The `ComponentProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
export interface ComponentProps {
    /**
     * Name identifier for this slot position.
     * Parent components can provide different content based on slot names.
     */
    name: string;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'FilterSlot'
     */
    testId?: string;
}

/**
 * The `FilterSlot` component serves as a development-only placeholder for filter slot positions within the application.
 * This component is designed to help developers identify and debug slot positions during development by rendering a hidden div element with identifying attributes.
 * In production builds, the component returns null to avoid any unnecessary DOM elements.
 *
 * @param props        The component props.
 * @param props.name   Name identifier for this slot position that helps identify the specific slot location.
 * @param props.testId Test identifier for component testing and debugging purposes.
 * @returns A hidden div element in development mode, or null in production mode.
 *
 * @example
 * ```tsx
 * <FilterSlot
 *     name="header-filter"
 *     testId="FilterSlot-header"
 * />
 * ```
 */
const FilterSlot = ({name, testId = 'FilterSlot'}: ComponentProps) => {
    if (process.env.NODE_ENV === 'development') {
        return <div data-cy={testId} data-slot-name={name} style={{display: 'none'}} />;
    }

    return null;
};

FilterSlot.displayName = 'FilterSlot';

export {FilterSlot};